import {
  forwardRef,
  useEffect,
  useState,
  type FormEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Link } from "react-router-dom";
import { page } from "../ui";

export function AdminShell({
  crumbs,
  title,
  children,
}: {
  crumbs: Array<{ label: string; to?: string }>;
  title: string;
  children: ReactNode;
}) {
  return (
    <div style={page}>
      <header
        style={{
          borderBottom: "1px solid #e6e6ee",
          paddingBottom: "0.6rem",
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <strong>WithAWill Learning Hub — Admin</strong>
        <a href="/logout">Sign out</a>
      </header>
      <nav style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.5rem" }}>
        {crumbs.map((c, i) => (
          <span key={i}>
            {i > 0 && " / "}
            {c.to ? <Link to={c.to}>{c.label}</Link> : c.label}
          </span>
        ))}
      </nav>
      <h1 style={{ marginTop: 0 }}>{title}</h1>
      {children}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const published = status === "PUBLISHED";
  return (
    <span
      style={{
        fontSize: "0.72rem",
        fontWeight: 600,
        padding: "0.1rem 0.45rem",
        borderRadius: 999,
        background: published ? "#e2f5e9" : "#f0f0f4",
        color: published ? "#0a7d33" : "#666",
      }}
    >
      {published ? "Published" : "Draft"}
    </span>
  );
}

export function Card({ children }: { children: ReactNode }) {
  return (
    <section
      style={{
        border: "1px solid #e6e6ee",
        borderRadius: 10,
        padding: "1rem 1.25rem",
        marginTop: "1rem",
      }}
    >
      {children}
    </section>
  );
}

export function Row({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.75rem",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0.4rem 0",
        borderBottom: "1px solid #f0f0f4",
      }}
    >
      {children}
    </div>
  );
}

export function Btn({
  children,
  onClick,
  disabled,
  kind = "primary",
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  kind?: "primary" | "ghost" | "danger";
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "0.35rem 0.8rem",
        borderRadius: 7,
        border:
          kind === "danger" ? "1px solid #b00020" : kind === "ghost" ? "1px solid #ccc" : "none",
        background:
          kind === "danger" || kind === "ghost" ? "#fff" : disabled ? "#9a9ab0" : "#1a1a2e",
        color: kind === "danger" ? "#b00020" : kind === "ghost" ? "#1a1a2e" : "#fff",
        cursor: disabled ? "default" : "pointer",
        fontSize: "0.85rem",
      }}
    >
      {children}
    </button>
  );
}

const labelStyle = { display: "block", fontSize: "0.8rem", color: "#555", marginBottom: "0.15rem" };
const inputStyle = {
  width: "100%",
  padding: "0.4rem 0.5rem",
  border: "1px solid #ccc",
  borderRadius: 6,
  fontSize: "0.9rem",
  boxSizing: "border-box" as const,
};

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: "0.7rem" }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

export function Text(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={inputStyle} />;
}
export const Area = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Area(props, ref) {
    return <textarea {...props} ref={ref} style={{ ...inputStyle, minHeight: 70 }} />;
  },
);
export function Sel({
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return (
    <select {...props} style={inputStyle}>
      {children}
    </select>
  );
}

/** Small inline form that calls `onSubmit`, shows errors. */
export function InlineForm({
  submitLabel,
  onSubmit,
  children,
}: {
  submitLabel: string;
  onSubmit: () => Promise<void>;
  children: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handle}>
      {children}
      {error && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{error}</p>}
      <Btn type="submit" disabled={busy}>
        {busy ? "Working…" : submitLabel}
      </Btn>
    </form>
  );
}

export function useAsync<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    load()
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
    // `deps` is spread intentionally — callers pass a stable list.
  }, [...deps, nonce]);

  return { data, error, reload: () => setNonce((n) => n + 1) };
}
